/**
 * One field for the AUM and the base currency it is held in: the amount on the left and the currency
 * select on the right, joined edge to edge out of the `mdotm` TextInput and Select chrome.
 */
import { MonetaryInput } from "$root/components/monetary-input";
import type { Currencies } from "@eu.mdotm.api/sphere";
import { Select, type SelectOption } from "@mdotm/shadcn/mdotm/components";
import { MAX_QUANTITY_AUM } from "../AumDialog";

export type AumCurrencyInputProps = {
	/** Worn by the AUM input, for the field label. */
	id?: string;
	invalid?: boolean;
	aum: number | null;
	onAumChange(aum: number | null): void;
	onAumBlur?(): void;
	currency: Currencies;
	onCurrencyChange(currency: Currencies): void;
	/** `undefined` while they load; the currency select stays disabled until then. */
	currencies: SelectOption<Currencies>[] | undefined;
	/** Prefix of the two parts' `data-qualifier`s: `<prefix>/Input` and `<prefix>/Currency`. */
	qualifier: string;
};

export function AumCurrencyInput({
	id,
	invalid,
	aum,
	onAumChange,
	onAumBlur,
	currency,
	onCurrencyChange,
	currencies,
	qualifier,
}: AumCurrencyInputProps): React.JSX.Element {
	return (
		<div className="flex">
			<MonetaryInput
				id={id}
				invalid={invalid}
				value={aum}
				onChange={onAumChange}
				onBlur={onAumBlur}
				min={0}
				max={MAX_QUANTITY_AUM}
				showCurrencyCode={false}
				className="flex-1 focus-within:z-10 [&>input]:rounded-r-none"
				data-qualifier={`${qualifier}/Input`}
			/>
			<Select
				options={currencies ?? []}
				value={currency}
				onChange={(next) => {
					if (next != null) {
						onCurrencyChange(next);
					}
				}}
				disabled={currencies == null}
				enableSearch
				align="end"
				className="relative -ml-px w-24 shrink-0 rounded-l-none focus:z-10"
				triggerDataAttrs={{ "data-qualifier": `${qualifier}/Currency` }}
			/>
		</div>
	);
}
